(() => {
  'use strict';

  const selector = 'video.lumerisa-hero-video[data-lumerisa-autoplay="true"]';
  const videos = new Set();
  let observer;

  const attemptPlay = (video) => {
    if (!video || document.hidden || !video.isConnected) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');

    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        // The browser can still block autoplay in Low Power/Data Saver modes.
        // The configured poster remains visible as the fallback.
      });
    }
  };

  const configureVideo = (video) => {
    if (!(video instanceof HTMLVideoElement) || videos.has(video)) return;

    videos.add(video);
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.controls = false;
    video.disablePictureInPicture = true;
    video.preload = 'metadata';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.removeAttribute('controls');

    const markReady = () => video.classList.add('is-ready');

    video.addEventListener('loadeddata', () => {
      markReady();
      attemptPlay(video);
    }, { once: true });
    video.addEventListener('canplay', () => attemptPlay(video), { once: true });
    video.addEventListener('playing', markReady, { once: true });

    if (observer) observer.observe(video);

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      markReady();
      attemptPlay(video);
    }
  };

  const scan = (root = document) => {
    root.querySelectorAll?.(selector).forEach(configureVideo);
  };

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > 0.05) {
            attemptPlay(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.05, 0.25] }
    );
  }

  const init = () => scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.addEventListener('pageshow', () => {
    scan(document);
    videos.forEach(attemptPlay);
  });

  document.addEventListener('visibilitychange', () => {
    videos.forEach((video) => {
      if (document.hidden) {
        if (!video.paused) video.pause();
      } else {
        attemptPlay(video);
      }
    });
  });

  const retryOnInteraction = () => {
    videos.forEach((video) => {
      if (video.paused) attemptPlay(video);
    });
  };
  window.addEventListener('click', retryOnInteraction, { once: true, passive: true });

  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  // Conversion-safety guards: avoid accidental page reloads from empty links and
  // keep broken optional media from blocking interaction or throwing repeatedly.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href=""], a[href="#"]');
    if (!link) return;
    const isIntentionalControl = link.hasAttribute('role') || link.hasAttribute('data-noop-link');
    if (!isIntentionalControl) event.preventDefault();
  });

  document.addEventListener('error', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement)) return;
    target.setAttribute('data-lumerisa-media-error', 'true');
    target.removeAttribute('srcset');
  }, true);

})();
